/**
 * Phone number parsing / validation, kept as pure functions so the FIELD and the screen's own
 * submit gate can agree without passing validity around.
 *
 * The single source of truth is the stored value, which is always **E.164** (`+923004197674`) —
 * digits only, no spaces, country code included. That is what the API, the rider's tel: link and any
 * future SMS gateway all need, and it is unambiguous no matter which country the shopper picked.
 * Grouping ("300 4197674") is presentation, applied on the way in and out of the input only.
 *
 * DELIBERATELY NOT libphonenumber: that library is ~500KB before tree-shaking for a storefront whose
 * traffic is overwhelmingly one country. The table below covers the markets orderYaa actually serves,
 * validates Pakistan properly, and length-checks the rest — which is honest about what it knows.
 */
export interface PhoneCountry {
  /** ISO 3166-1 alpha-2, used as the stable option value. */
  iso: string;
  name: string;
  /** Country calling code, no '+'. */
  dial: string;
  flag: string;
  /** National significant number length bounds (digits after the country code). */
  nsnMin: number;
  nsnMax: number;
  /**
   * Leading digits a MOBILE number must start with. Empty = we don't claim to know, so length is the
   * only check. Deliveries are texted and called, so a landline is a real failure worth catching.
   */
  mobilePrefixes: string[];
  /** Digit grouping for display, e.g. [3, 7] renders "300 4197674". */
  groups: number[];
  /** Shown as the input placeholder — a real, correctly-shaped number for that country. */
  example: string;
}

/**
 * Pakistan first because it is the default and the overwhelming majority of traffic. The others are
 * where Pakistani customers order from / to most often. Add a row rather than reaching for a library.
 */
export const PHONE_COUNTRIES: PhoneCountry[] = [
  { iso: 'PK', name: 'Pakistan',             dial: '92',  flag: '🇵🇰', nsnMin: 10, nsnMax: 10, mobilePrefixes: ['3'],       groups: [3, 7], example: '300 4197674' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '971', flag: '🇦🇪', nsnMin: 9,  nsnMax: 9,  mobilePrefixes: ['5'],       groups: [2, 3, 4], example: '50 123 4567' },
  { iso: 'SA', name: 'Saudi Arabia',         dial: '966', flag: '🇸🇦', nsnMin: 9,  nsnMax: 9,  mobilePrefixes: ['5'],       groups: [2, 3, 4], example: '50 123 4567' },
  { iso: 'GB', name: 'United Kingdom',       dial: '44',  flag: '🇬🇧', nsnMin: 10, nsnMax: 10, mobilePrefixes: ['7'],       groups: [4, 6], example: '7400 123456' },
  { iso: 'US', name: 'United States',        dial: '1',   flag: '🇺🇸', nsnMin: 10, nsnMax: 10, mobilePrefixes: [],          groups: [3, 3, 4], example: '201 555 0123' },
  { iso: 'CA', name: 'Canada',               dial: '1',   flag: '🇨🇦', nsnMin: 10, nsnMax: 10, mobilePrefixes: [],          groups: [3, 3, 4], example: '204 555 0123' },
];

export const DEFAULT_PHONE_COUNTRY = PHONE_COUNTRIES[0];   // Pakistan

export function countryByIso(iso: string): PhoneCountry {
  return PHONE_COUNTRIES.find((c) => c.iso === iso) ?? DEFAULT_PHONE_COUNTRY;
}

/** Everything that is not a digit, gone. */
export function digitsOnly(value: string | null | undefined): string {
  return (value ?? '').replace(/\D+/g, '');
}

/**
 * The national number a shopper actually typed, cleaned up.
 *
 * Handles the three things people really do: type the trunk zero (`0300…`), paste the full
 * international form (`+92300…` or `0092300…`), or type it bare (`300…`). All three must land on the
 * same stored value, because the same person will do a different one of them next time.
 */
export function normaliseNational(input: string, country: PhoneCountry): string {
  let d = digitsOnly(input);
  if (!d) return '';

  // 00 → international prefix.
  if (d.startsWith('00')) d = d.slice(2);
  // Country code typed or pasted in the national box — drop it, but ONLY if what remains can still be
  // a national number. Otherwise "92…" might legitimately be the start of one.
  if (d.startsWith(country.dial) && d.length > country.dial.length) {
    const rest = d.slice(country.dial.length);
    if (rest.length >= country.nsnMin) d = rest;
  }
  // Trunk zero: national dialling only, never part of the international form.
  if (d.startsWith('0')) d = d.replace(/^0+/, '');

  return d.slice(0, country.nsnMax);
}

/** Is this a complete, plausible mobile number for that country? */
export function isValidNational(national: string, country: PhoneCountry): boolean {
  const d = digitsOnly(national);
  if (d.length < country.nsnMin || d.length > country.nsnMax) return false;
  if (country.mobilePrefixes.length === 0) return true;
  return country.mobilePrefixes.some((p) => d.startsWith(p));
}

/** `+923004197674`, or '' when incomplete — an incomplete number must never be stored as if real. */
export function toE164(national: string, country: PhoneCountry): string {
  const d = digitsOnly(national);
  return isValidNational(d, country) ? `+${country.dial}${d}` : '';
}

/** Split a stored E.164 value back into a country + national number, for editing. */
export function fromE164(value: string | null | undefined): { country: PhoneCountry; national: string } {
  const raw = (value ?? '').trim();
  const d = digitsOnly(raw);
  if (!d) return { country: DEFAULT_PHONE_COUNTRY, national: '' };

  // Only trust a country code when the value was actually written in international form; a bare
  // "3004197674" is a national number, and '1' or '92' would otherwise be read off the front of it.
  if (raw.startsWith('+') || d.startsWith('00')) {
    const intl = d.startsWith('00') ? d.slice(2) : d;
    // Longest dial code first, so '971' is not mistaken for '9' or '97'.
    const byLongest = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
    for (const c of byLongest) {
      if (!intl.startsWith(c.dial)) continue;
      const nsn = intl.slice(c.dial.length);
      if (nsn.length >= c.nsnMin && nsn.length <= c.nsnMax) return { country: c, national: nsn };
    }
  }
  return { country: DEFAULT_PHONE_COUNTRY, national: normaliseNational(d, DEFAULT_PHONE_COUNTRY) };
}

/** "3004197674" → "300 4197674". Groups progressively, so it reads correctly while still being typed. */
export function formatNational(national: string, country: PhoneCountry): string {
  const d = digitsOnly(national);
  if (!d) return '';
  const parts: string[] = [];
  let i = 0;
  for (const size of country.groups) {
    if (i >= d.length) break;
    parts.push(d.slice(i, i + size));
    i += size;
  }
  if (i < d.length) parts.push(d.slice(i));   // anything beyond the pattern stays visible
  return parts.join(' ');
}
